type CompactOptions = {
  allowEmptyArray?: boolean,
  allowEmptyObject?: boolean,
  allowEmptyString?: boolean
}

/**
 * Recursively removes null, undefined, and optionally empty objects, arrays, or strings
 * from the provided input. The behavior can be customized through the options parameter.
 *
 * @param {T} input The input value to be processed. This can be of any data type including
 * string, object, array, or other primitives.
 * @param {CompactOptions} [options={}] An optional configuration object to customize
 * the function's behavior.
 * @param {boolean} [options.allowEmptyArray=false] If true, empty arrays will not be removed.
 * @param {boolean} [options.allowEmptyObject=false] If true, empty objects will not be removed.
 * @param {boolean} [options.allowEmptyString=false] If true, empty strings will not be removed.
 * @return {any | null} A "compacted" version of the input value according to the specified
 * rules, or `null` if the input is removed based on the criteria.
 */
export function compact<T>(input: T, options: CompactOptions = {}): any | null {
  const {
    allowEmptyArray = false,
    allowEmptyObject = false,
    allowEmptyString = false,
  } = options

  // Remove null / undefined
  if (input === null || input === undefined) return null

  // Remove empty string unless allowed
  if (typeof input === "string") {
    if (input.trim() === "" && !allowEmptyString) return null
    return input
  }

  // Arrays
  if (Array.isArray(input)) {
    const cleaned = input
      .map((v) => compact(v, options))
      .filter((v) => v !== null)

    if (cleaned.length === 0 && !allowEmptyArray) return null
    return cleaned
  }

  // Objects
  if (typeof input === "object") {
    const result: Record<string, any> = {}

    for (const [key, value] of Object.entries(input as any)) {
      const cleanedValue = compact(value, options)
      if (cleanedValue !== null) {
        result[key] = cleanedValue
      }
    }

    const keys = Object.keys(result)
    if (keys.length === 0 && !allowEmptyObject) return null

    return result
  }

  // Keep other primitives (number, boolean, etc.)
  return input
}
